import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Users2, Eye, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { errMsg } from "./AttendanceTab";

const chip = (on) => `h-9 px-3.5 rounded-full text-xs font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`;
const when = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function PeoplePicker({ staff, picked, setPicked }) {
  const [q, setQ] = useState("");
  const rows = staff.filter((u) => (u.name || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <div className="border border-slate-200 rounded-xl">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" className="h-9 border-0 border-b rounded-none" data-testid="announce-people-search" />
      <div className="max-h-40 overflow-y-auto p-1">
        {rows.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
            <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} className="accent-violet-600" data-testid={`announce-person-${u.id}`} />
            <span className="flex-1 truncate">{u.name}</span><span className="text-[11px] text-slate-400">{u.department || u.designation || ""}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Compose({ staff, onSent }) {
  const [f, setF] = useState({ subject: "", message: "", audience: "all", department: "" });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const depts = useMemo(() => [...new Set(staff.map((u) => (u.department || "").trim()).filter(Boolean))].sort(), [staff]);
  const count = f.audience === "all" ? staff.length : f.audience === "department" ? staff.filter((u) => (u.department || "").trim() === f.department).length : picked.length;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/announcements", { ...f, user_ids: picked });
      toast.success(`Sent to ${data.recipient_count} staff`);
      setF({ subject: "", message: "", audience: "all", department: "" }); setPicked([]); onSent();
    } catch (e) { toast.error(errMsg(e, "Could not send")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="announce-compose">
      <div><Label className="text-xs">Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="e.g. Office closed on Friday" className="h-10" data-testid="announce-subject" /></div>
      <div><Label className="text-xs">Message</Label><Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Write the full message for staff" data-testid="announce-message" /></div>
      <div>
        <Label className="text-xs">Send to</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {[["all", "All staff"], ["department", "A department"], ["people", "Chosen people"]].map(([k, l]) => <button key={k} type="button" onClick={() => setF({ ...f, audience: k })} className={chip(f.audience === k)} data-testid={`announce-aud-${k}`}>{l}</button>)}
        </div>
      </div>
      {f.audience === "department" && (
        <div className="flex flex-wrap gap-1.5" data-testid="announce-depts">
          {depts.length ? depts.map((d) => <button key={d} type="button" onClick={() => setF({ ...f, department: d })} className={chip(f.department === d)} data-testid={`announce-dept-${d}`}>{d}</button>)
            : <span className="text-xs text-slate-500">No departments set on staff profiles yet.</span>}
        </div>
      )}
      {f.audience === "people" && <PeoplePicker staff={staff} picked={picked} setPicked={setPicked} />}
      <Button onClick={send} disabled={busy || !f.subject.trim() || !f.message.trim() || !count} className="w-full h-11 bg-violet-600 hover:bg-violet-700 text-white" data-testid="announce-send">
        <Send className="h-4 w-4 mr-1.5" />Send to {count} staff
      </Button>
    </div>
  );
}

function Reads({ a, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/hr/announcements/${a.id}/reads`).then(({ data }) => setD(data)).catch(() => setD({ read: [], unread: [] })); }, [a.id]);
  const list = (rows, tone, label, id) => (
    <div data-testid={id}><div className={`text-[10px] uppercase tracking-wider font-bold ${tone}`}>{label} · {rows.length}</div>
      <div className="flex flex-wrap gap-1.5 mt-1.5">{rows.map((r) => <span key={r.id} className="text-xs bg-slate-100 rounded-full px-2.5 py-1" title={r.read_at ? `Read ${when(r.read_at)}` : ""}>{r.name}</span>)}</div></div>
  );
  return (
    <div className="space-y-3" data-testid="announce-reads">
      <button onClick={onBack} className="text-xs text-violet-700 font-semibold hover:underline" data-testid="announce-reads-back">← All announcements</button>
      <div><div className="font-bold text-slate-900">{a.subject}</div><div className="text-xs text-slate-500 whitespace-pre-line mt-1">{a.message}</div></div>
      {!d ? <div className="text-sm text-slate-500">Loading…</div> : <>{list(d.read, "text-emerald-700", "Read", "announce-read-list")}{list(d.unread, "text-amber-700", "Not read yet", "announce-unread-list")}</>}
    </div>
  );
}

function History({ rows, onPick, onDelete }) {
  if (!rows.length) return <div className="text-sm text-slate-500 py-4 text-center">No announcements sent yet.</div>;
  return (
    <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl" data-testid="announce-history">
      {rows.map((a) => {
        const pct = a.recipient_count ? Math.round((a.read_count / a.recipient_count) * 100) : 0;
        return (
          <div key={a.id} className="px-3 py-2.5 flex items-center gap-2">
            <button onClick={() => onPick(a)} className="flex-1 min-w-0 text-left" data-testid={`announce-row-${a.id}`}>
              <div className="text-sm font-semibold text-slate-900 truncate">{a.subject}</div>
              <div className="text-[11px] text-slate-500">{when(a.created_at)} · {a.audience === "department" ? a.department : a.audience === "people" ? "chosen people" : "all staff"} · by {a.by}</div>
              <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
            </button>
            <span className="text-xs font-bold text-emerald-700 shrink-0 inline-flex items-center gap-1" data-testid={`announce-readcount-${a.id}`}><Eye className="h-3.5 w-3.5" />{a.read_count}/{a.recipient_count}</span>
            <button onClick={() => onDelete(a)} className="h-8 w-8 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 inline-flex items-center justify-center" title="Delete" data-testid={`announce-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
}

export function AnnouncementsDialog({ onClose }) {
  const [tab, setTab] = useState("new");
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState(null);
  const loadRows = () => api.get("/hr/announcements").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff((data || []).filter((u) => !u.exclude_from_hr && !(u.last_working_day && u.last_working_day < new Date().toISOString().slice(0, 10))))).catch(() => {});
    loadRows();
  }, []);
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.subject}"? Staff will no longer see it.`)) return;
    await api.delete(`/hr/announcements/${a.id}`).catch(() => {});
    loadRows();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[92vh] overflow-y-auto" data-testid="announce-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Megaphone className="h-5 w-5 text-violet-600" />Staff announcements</DialogTitle>
          <DialogDescription>Shows up in each person's HR alerts.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5">
          <button onClick={() => { setTab("new"); setPick(null); }} className={chip(tab === "new")} data-testid="announce-tab-new"><Send className="h-3.5 w-3.5 inline mr-1" />New</button>
          <button onClick={() => setTab("sent")} className={chip(tab === "sent")} data-testid="announce-tab-sent"><Users2 className="h-3.5 w-3.5 inline mr-1" />Sent & read by</button>
        </div>
        {tab === "new" ? <Compose staff={staff} onSent={() => { loadRows(); setTab("sent"); }} />
          : pick ? <Reads a={pick} onBack={() => { setPick(null); loadRows(); }} /> : <History rows={rows} onPick={setPick} onDelete={del} />}
      </DialogContent>
    </Dialog>
  );
}
